/**
 * Cross-tab auth event broadcaster.
 * Uses BroadcastChannel when available, falls back to localStorage `storage`
 * events (for older Safari WebViews that don't expose BroadcastChannel).
 */

export type AuthEvent =
  | { type: "logout"; reason: "inactivity" | "manual" | "remote"; ts: number };

const CHANNEL = "acex-auth";
const STORAGE_KEY = "acex:auth-event";

let bc: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (typeof window === "undefined") return null;
  if (bc) return bc;
  try {
    if ("BroadcastChannel" in window) {
      bc = new BroadcastChannel(CHANNEL);
    }
  } catch {
    bc = null;
  }
  return bc;
}

export function broadcastLogout(reason: AuthEvent["reason"]) {
  const evt: AuthEvent = { type: "logout", reason, ts: Date.now() };
  const ch = getChannel();
  try {
    ch?.postMessage(evt);
  } catch {
    /* ignore */
  }
  // Always also write to localStorage for cross-tab fallback + persistence
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(evt));
  } catch {
    /* ignore */
  }
}

export function subscribeAuthEvents(handler: (evt: AuthEvent) => void): () => void {
  const ch = getChannel();
  const onMsg = (e: MessageEvent) => {
    if (e?.data && typeof e.data === "object" && (e.data as AuthEvent).type) {
      handler(e.data as AuthEvent);
    }
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      const evt = JSON.parse(e.newValue) as AuthEvent;
      if (evt && evt.type) handler(evt);
    } catch {
      /* ignore */
    }
  };
  ch?.addEventListener("message", onMsg);
  window.addEventListener("storage", onStorage);
  return () => {
    ch?.removeEventListener("message", onMsg);
    window.removeEventListener("storage", onStorage);
  };
}
